import { Button, SelectBox, TagBox } from "devextreme-react";
import DataGrid, { Column, Editing, FilterPanel, FilterRow, HeaderFilter, Selection, StateStoring } from "devextreme-react/data-grid";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createUserProgramCategories, deleteUserProgramCategories, fetchUserProgramCategories, fetchAllCategoriesByProgram } from "../../../../actions";

export const PermissionsByUser = (props) => {
    const { userId } = props;

    const [state, setstate] = useState({ "programId": 0, "categoryIds": [], "addButtonVisible": false });
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const programs = useSelector(state => state.admin.programs)
    const categories = useSelector(state => state.admin.categories_by_program)
    const userProgramsCategories = useSelector(state => state.admin.programCategories);
    const isLoading = useSelector(state => state.admin.loading)
    const dispatch = useDispatch();

    useEffect(() => {
        if (userId!==0){
            dispatch(fetchUserProgramCategories(userId));
        }      
    }, [userId, dispatch]);

    /**
     * Developer: CNino
     * Date: 2022-11-01
     * Remarks: The call of the categories is added according to the program selected by the user
     * @param {*} e 
     */
    const setProgramId = useCallback((e) => {
        setstate(prev => ({ ...prev, "programId": e, "categoryIds": [], "addButtonVisible": ((e !== 0)) }));
        dispatch(fetchAllCategoriesByProgram(e));
    }, [dispatch]);
    
    const setCategoryIds = useCallback((e) => {
        setstate(prev => ({ ...prev, "categoryIds": e, "addButtonVisible": ((prev.programId !== 0) && (e.length > 0)) }));
    }, []);

    const saveUserProgramCategories = useCallback(() => {
        if (state.programId && state.categoryIds.length > 0) {
            // Add all selected categories for the program
            state.categoryIds.forEach(categoryId => {
                dispatch(createUserProgramCategories(userId, state.programId, categoryId));
            });
            // Reset category selection after adding
            setstate(prev => ({ ...prev, "categoryIds": [], "addButtonVisible": false }));
        }
    }, [state.programId, state.categoryIds, userId, dispatch]);

    // Prepare grid data with composite keys - memoized
    const gridDataSource = useMemo(() => {
        return userProgramsCategories?.map(row => ({
            ...row,
            compositeKey: `${row.idUser}_${row.idProgram}_${row.idCategory}`
        })) || [];
    }, [userProgramsCategories]);

    const deleteSelectedPermissions = useCallback(() => {
        if (selectedRowKeys.length > 0 && userProgramsCategories) {
            // Find the full row data for each selected key
            const rowsToDelete = gridDataSource.filter(row => 
                selectedRowKeys.includes(row.compositeKey)
            );
            
            // Delete all selected rows
            const deletePromises = rowsToDelete.map(row => 
                dispatch(deleteUserProgramCategories(row.idUser, row.idProgram, row.idCategory))
            );
            
            // After all deletions complete, refetch the data
            Promise.all(deletePromises).then(() => {
                dispatch(fetchUserProgramCategories(userId));
                setSelectedRowKeys([]);
            });
        }
    }, [selectedRowKeys, userProgramsCategories, gridDataSource, dispatch, userId]);

    const onSaving = useCallback((e) => {
        const change = e.changes[0];
        if (change.type === "remove") {
            const compositeKey = change.key;
            // Find the row data using the composite key
            const deletedRow = gridDataSource.find(row => row.compositeKey === compositeKey);
            if (deletedRow) {
                dispatch(deleteUserProgramCategories(deletedRow.idUser, deletedRow.idProgram, deletedRow.idCategory))
                    .then(() => {
                        dispatch(fetchUserProgramCategories(userId));
                    });
            }
        }
    }, [gridDataSource, dispatch, userId]);

    // Filter out categories that user already has for the selected program - memoized
    const availableCategories = useMemo(() => {
        return categories.filter(category => {
            return !userProgramsCategories?.some(permission => 
                permission.idProgram === state.programId && 
                permission.idCategory === category.categoryId
            );
        });
    }, [categories, userProgramsCategories, state.programId]);

    // Early returns after all hooks
    if (isLoading) {
        return <></>
    }

    if (userId===0){
        return <></>
    }

    return (
        <>
            <div style={{ display: "flex" }}>
                <div>
                    <div>Programs</div>
                    <div>
                        <SelectBox 
                            dataSource={programs}
                            displayExpr="name"
                            valueExpr="programId"
                            onValueChange={setProgramId}
                            defaultValue={state.programId}
                        />
                    </div>
                </div>
                <div style={{ paddingLeft: "1vw", flex: 1 }}>
                    <div>Categories (Select Multiple)</div>
                    <div>
                        <TagBox 
                            dataSource={availableCategories}
                            displayExpr="categoryName"
                            valueExpr="categoryId"
                            value={state.categoryIds}
                            onValueChanged={(e) => setCategoryIds(e.value)}
                            searchEnabled={true}
                            showSelectionControls={true}
                            applyValueMode="useButtons"
                            placeholder="Select categories..."
                            disabled={state.programId === 0}
                            noDataText="All categories already assigned"
                        />
                    </div>
                </div>
            </div>
            <div style={{ paddingTop: "1vh", display: "flex", gap: "10px" }}>
                <Button 
                    icon="plus" 
                    visible={state.addButtonVisible} 
                    onClick={saveUserProgramCategories}
                    text={`Add ${state.categoryIds.length} Permission${state.categoryIds.length > 1 ? 's' : ''}`}
                />
                <Button 
                    icon="trash" 
                    visible={selectedRowKeys.length > 0}
                    onClick={deleteSelectedPermissions}
                    text={`Delete ${selectedRowKeys.length} Selected`}
                    type="danger"
                />
            </div>
            <div id="kitsGrid" style={{ height: "60vh", paddingTop: "1vh" }} >
                <DataGrid
                    id="gridContainer"
                    dataSource={gridDataSource}
                    showBorders={true}
                    height={"100%"}
                    onSaving={onSaving}
                    selectedRowKeys={selectedRowKeys}
                    onSelectionChanged={(e) => setSelectedRowKeys(e.selectedRowKeys)}
                    keyExpr="compositeKey"
                >
                    <FilterRow visible={true} />
                    <FilterPanel visible={true} />
                    <HeaderFilter visible={true} />
                    <StateStoring enabled={true} type="localStorage" />
                    <Selection mode="multiple" showCheckBoxesMode="always" />
                    <Editing
                        allowDeleting={true}
                        useIcons={true}
                    >
                    </Editing>
                    <Column dataField="idUser" caption="idUser" visible={false} />
                    <Column dataField="idProgram" caption="ProgramId" visible={false} />
                    <Column dataField="program" caption="Program" allowEditing={false} />
                    <Column dataField="idCategory" caption="categoryId" visible={false} />
                    <Column dataField="category" caption="Category" allowEditing={false} />
                </DataGrid>

            </div>
        </>

    );
}
export default PermissionsByUser;